import { useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContentTypeFilter } from './ContentTypeFilter';
import { CommunitiesTypeFilter } from './CommunitiesTypeFilter';
import { LocationAndZoneFilters } from './LocationAndZoneFilters';
import { FilterResultsSummary } from './FilterResultsSummary';
import { ZoneToggle } from '../ZoneToggle';

interface Community {
  id: string;
  name: string;
  members_count?: number;
}

interface MobileFiltersSheetProps {
  open: boolean;
  onClose: () => void;
  postTypeFilter: string;
  onPostTypeFilterChange: (value: string) => void;
  selectedCommunity: string;
  onCommunityChange: (value: string) => void;
  locationFilter: string;
  onLocationFilterChange: (value: string) => void;
  filteredCount: number;
  totalCount: number;
  zoneFilters: {
    activeTab: 'climate' | 'hardiness';
    climateZone?: string;
    country?: string;
    hardinessZone?: string;
    showMyZoneOnly: boolean;
  };
  onZoneFiltersChange: (filters: any) => void;
}

export const MobileFiltersSheet = ({ 
  open, 
  onClose,
  postTypeFilter,
  onPostTypeFilterChange,
  selectedCommunity,
  onCommunityChange,
  locationFilter,
  onLocationFilterChange,
  filteredCount,
  totalCount,
  zoneFilters,
  onZoneFiltersChange
}: MobileFiltersSheetProps) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = prev || '';
    }
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-labelledby="filters-title">
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm" 
        onClick={onClose}
        aria-hidden="true"
      />
      <div 
        id="feed-filters"
        className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white dark:bg-gray-800 p-4 ys-mobile-sheet overflow-y-auto shadow-2xl"
      >
        {/* Handle bar */}
        <div className="h-1 w-10 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-4" />
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 id="filters-title" className="text-lg font-semibold">Filters</h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="h-8 w-8 p-0 ys-touch-target"
            aria-label="Close filters"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Filter content */}
        <div className="space-y-3">
          <ContentTypeFilter 
            postTypeFilter={postTypeFilter}
            onPostTypeFilterChange={onPostTypeFilterChange}
          />

          <CommunitiesTypeFilter 
            selectedCommunity={selectedCommunity}
            onCommunityChange={onCommunityChange}
          />

          {/* Location and Climate Zone side by side */}
          <div className="grid grid-cols-3 gap-2">
            <LocationAndZoneFilters 
              className="col-span-2"
              locationFilter={locationFilter}
              onLocationFilterChange={onLocationFilterChange}
            />
            
            <ZoneToggle 
              showMyZoneOnly={zoneFilters.showMyZoneOnly}
              onToggleChange={(checked) => {
                onZoneFiltersChange({ ...zoneFilters, showMyZoneOnly: checked });
              }}
            />
          </div>

          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <FilterResultsSummary 
              postTypeFilter={postTypeFilter}
              selectedCommunity={selectedCommunity}
              locationFilter={locationFilter}
              filteredCount={filteredCount}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
